/**
 * Market Context
 * Controls business region settings: US vs Brazil
 *
 * This determines:
 * - Language (en-US / pt-BR)
 * - Currency (USD / BRL)
 * - Tax system (US / Brazilian ICMS, PIS, COFINS)
 * - ID fields (EIN / CNPJ)
 * - Document types (Invoice / NF-e)
 * - Date/number formats
 * - Payment methods
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

export type Market = 'US' | 'BR';

interface MarketConfig {
  market: Market;
  locale: string;
  currency: string;
  currencySymbol: string;
  taxIdLabel: string;
  taxIdPlaceholder: string;
  documentType: string;
  dateFormat: string;
  flag: string;
  name: string;
  paymentMethods: string[];
}

const MARKET_CONFIGS: Record<Market, MarketConfig> = {
  US: {
    market: 'US',
    locale: 'en-US',
    currency: 'USD',
    currencySymbol: '$',
    taxIdLabel: 'EIN',
    taxIdPlaceholder: '12-3456789',
    documentType: 'Invoice',
    dateFormat: 'MM/DD/YYYY',
    flag: '🇺🇸',
    name: 'United States',
    paymentMethods: ['ACH', 'Wire', 'Check', 'Credit Card'],
  },
  BR: {
    market: 'BR',
    locale: 'pt-BR',
    currency: 'BRL',
    currencySymbol: 'R$',
    taxIdLabel: 'CNPJ',
    taxIdPlaceholder: '12.345.678/0001-99',
    documentType: 'NF-e',
    dateFormat: 'DD/MM/YYYY',
    flag: '🇧🇷',
    name: 'Brasil',
    paymentMethods: ['PIX', 'Boleto', 'TED', 'Cartão'],
  },
};

// English translations (fallback)
const enUS: Record<string, unknown> = {
  common: {
    dashboard: 'Dashboard',
    vendors: 'Vendors',
    customers: 'Customers',
    invoices: 'Invoices',
    bills: 'Bills',
    settings: 'Settings',
    profile: 'Profile',
    help: 'Help',
    notifications: 'Notifications',
    logout: 'Logout',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    viewAll: 'View all',
    loading: 'Loading...',
    noData: 'No data available',
    welcome: 'Welcome back',
  },
  dashboard: {
    title: 'Dashboard',
    financialOverview: "Here's your financial overview for",
    totalRevenue: 'Monthly Revenue',
    accountsReceivable: 'Accounts Receivable',
    accountsPayable: 'Accounts Payable',
    netCashPosition: 'Net Cash Position',
    overdue: 'overdue',
    pending: 'pending',
    open: 'open',
    vsLastMonth: 'vs last month',
    cashFlow: 'Cash Flow',
    sixMonthOverview: '6-month overview',
    inflows: 'Inflows',
    outflows: 'Outflows',
    totalInflows: 'Total Inflows',
    totalOutflows: 'Total Outflows',
    netCashFlow: 'Net Cash Flow',
    revenueTrend: 'Revenue Trend',
    actualVsTarget: 'Actual vs Target',
    aboveTarget: 'Above target',
    actualRevenue: 'Actual Revenue',
    target: 'Target',
    receivablesAging: 'Receivables Aging',
    payablesAging: 'Payables Aging',
    current: 'Current',
    days30: '1-30 days',
    days60: '31-60 days',
    days90: '60+ days',
    financialModules: 'Financial Modules',
    generalLedger: 'General Ledger',
    chartOfAccounts: 'Chart of accounts',
    vendorBills: 'Vendor bills',
    customerInvoices: 'Customer invoices',
    cashBanking: 'Cash & Banking',
    bankReconciliation: 'Bank reconciliation',
    accounts: 'accounts',
    topCustomers: 'Top Customers',
    topVendors: 'Top Vendors',
    byOutstandingBalance: 'By outstanding balance',
    recentActivity: 'Recent Activity',
    aiPowered: 'AI-Powered',
    newBill: 'New Bill',
    newInvoice: 'New Invoice',
  },
  ap: {
    title: 'Accounts Payable',
    bills: 'Bills',
    billsDescription: 'Manage vendor bills and payments',
    newBill: 'New Bill',
    createBill: 'Create Bill',
    editBill: 'Edit Bill',
    vendor: 'Vendor',
    selectVendor: 'Select vendor',
    amount: 'Amount',
    dueDate: 'Due Date',
    status: 'Status',
    description: 'Description',
    category: 'Category',
    reference: 'Reference',
    notes: 'Notes',
    draft: 'Draft',
    pending: 'Pending',
    approved: 'Approved',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    vendors: 'Vendors',
    vendorsDescription: 'Manage your vendor relationships',
    newVendor: 'New Vendor',
    createVendor: 'Create Vendor',
    vendorName: 'Vendor Name',
    contactName: 'Contact Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    state: 'State',
    zipCode: 'ZIP Code',
    country: 'Country',
    paymentTerms: 'Payment Terms',
    openBalance: 'Open Balance',
    totalPurchases: 'Total Purchases',
  },
  ar: {
    title: 'Accounts Receivable',
    invoices: 'Invoices',
    invoicesDescription: 'Manage customer invoices',
    newInvoice: 'New Invoice',
    createInvoice: 'Create Invoice',
    editInvoice: 'Edit Invoice',
    customer: 'Customer',
    selectCustomer: 'Select customer',
    invoiceNumber: 'Invoice Number',
    issueDate: 'Issue Date',
    dueDate: 'Due Date',
    amount: 'Amount',
    paidAmount: 'Paid Amount',
    balance: 'Balance',
    status: 'Status',
    draft: 'Draft',
    sent: 'Sent',
    viewed: 'Viewed',
    paid: 'Paid',
    overdue: 'Overdue',
    void: 'Void',
    customers: 'Customers',
    customersDescription: 'Manage your customer relationships',
    newCustomer: 'New Customer',
    createCustomer: 'Create Customer',
    customerName: 'Customer Name',
    contactName: 'Contact Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    state: 'State',
    zipCode: 'ZIP Code',
    country: 'Country',
    creditLimit: 'Credit Limit',
    openBalance: 'Open Balance',
    totalRevenue: 'Total Revenue',
  },
  ai: {
    automation: 'AI-Powered Automation',
    realTimeMetrics: 'Real-time processing metrics',
    allSystemsOperational: 'All Systems Operational',
    touchlessProcessing: 'Touchless Processing',
    autoProcessed: 'Auto-processed transactions',
    categorization: 'AI Categorization',
    accuracyRate: 'Accuracy rate',
    receiptProcessing: 'Receipt Processing',
    avgExtractionTime: 'Avg. extraction time',
    invoiceExtraction: 'Invoice Extraction',
    dataAccuracy: 'Data accuracy',
    onTarget: 'On Target',
    scanReceipt: 'Scan Receipt',
    extractInvoice: 'Extract Invoice',
    smartCategorize: 'Smart Categorize',
    poweredBy: 'Powered by Finova AI',
  },
};

// Portuguese translations (Brazilian Portuguese - pt-BR)
// Note: Proper accents are critical for professional appearance
const ptBR: Record<string, unknown> = {
  common: {
    dashboard: 'Painel',
    vendors: 'Fornecedores',
    customers: 'Clientes',
    invoices: 'Notas Fiscais',
    bills: 'Contas a Pagar',
    settings: 'Configurações',
    profile: 'Perfil',
    help: 'Ajuda',
    notifications: 'Notificações',
    logout: 'Sair',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    create: 'Criar',
    search: 'Buscar',
    filter: 'Filtrar',
    export: 'Exportar',
    import: 'Importar',
    viewAll: 'Ver todos',
    loading: 'Carregando...',
    noData: 'Nenhum dado disponível',
    welcome: 'Bem-vindo de volta',
    actions: 'Ações',
    view: 'Visualizar',
    status: 'Situação',
    active: 'Ativo',
    inactive: 'Inativo',
    onHold: 'Em Espera',
    // Sidebar translations
    accountsPayable: 'Contas a Pagar',
    accountsReceivable: 'Contas a Receber',
    expandSidebar: 'Expandir menu',
    collapseSidebar: 'Recolher menu',
  },
  dashboard: {
    title: 'Painel',
    financialOverview: 'Aqui está sua visão financeira para',
    totalRevenue: 'Receita Mensal',
    accountsReceivable: 'Contas a Receber',
    accountsPayable: 'Contas a Pagar',
    netCashPosition: 'Posição Líquida de Caixa',
    overdue: 'vencido',
    pending: 'pendente',
    open: 'em aberto',
    vsLastMonth: 'vs mês anterior',
    cashFlow: 'Fluxo de Caixa',
    sixMonthOverview: 'Visão dos últimos 6 meses',
    inflows: 'Entradas',
    outflows: 'Saídas',
    totalInflows: 'Total de Entradas',
    totalOutflows: 'Total de Saídas',
    netCashFlow: 'Fluxo de Caixa Líquido',
    revenueTrend: 'Tendência de Receita',
    actualVsTarget: 'Realizado vs Meta',
    aboveTarget: 'Acima da meta',
    belowTarget: 'Abaixo da meta',
    onTarget: 'Dentro da meta',
    actualRevenue: 'Receita Realizada',
    target: 'Meta',
    receivablesAging: 'Vencimento de Recebíveis',
    payablesAging: 'Vencimento de Pagáveis',
    current: 'Em dia',
    days30: '1-30 dias',
    days60: '31-60 dias',
    days90: '60+ dias',
    financialModules: 'Módulos Financeiros',
    generalLedger: 'Razão Contábil',
    chartOfAccounts: 'Plano de Contas',
    vendorBills: 'Contas de Fornecedores',
    customerInvoices: 'Notas Fiscais de Clientes',
    cashBanking: 'Caixa e Bancos',
    bankReconciliation: 'Conciliação Bancária',
    accounts: 'contas',
    topCustomers: 'Principais Clientes',
    topVendors: 'Principais Fornecedores',
    byOutstandingBalance: 'Por saldo em aberto',
    recentActivity: 'Atividade Recente',
    aiPowered: 'Potencializado por IA',
    newBill: 'Nova Conta',
    newInvoice: 'Nova NF-e',
  },
  ap: {
    title: 'Contas a Pagar',
    bills: 'Contas',
    billsDescription: 'Gerenciar contas e pagamentos de fornecedores',
    newBill: 'Nova Conta',
    createBill: 'Criar Conta',
    editBill: 'Editar Conta',
    vendor: 'Fornecedor',
    selectVendor: 'Selecionar fornecedor',
    amount: 'Valor',
    dueDate: 'Data de Vencimento',
    status: 'Situação',
    description: 'Descrição',
    category: 'Categoria',
    reference: 'Referência',
    notes: 'Observações',
    draft: 'Rascunho',
    pending: 'Pendente',
    approved: 'Aprovado',
    paid: 'Pago',
    overdue: 'Vencido',
    cancelled: 'Cancelado',
    vendors: 'Fornecedores',
    vendorsDescription: 'Gerenciar relacionamentos com fornecedores',
    newVendor: 'Novo Fornecedor',
    createVendor: 'Criar Fornecedor',
    vendorName: 'Nome do Fornecedor',
    contactName: 'Nome do Contato',
    email: 'E-mail',
    phone: 'Telefone',
    address: 'Endereço',
    city: 'Cidade',
    state: 'Estado',
    zipCode: 'CEP',
    country: 'País',
    paymentTerms: 'Condições de Pagamento',
    openBalance: 'Saldo em Aberto',
    totalPurchases: 'Total de Compras',
    totalOutstanding: 'Total Pendente',
    manageVendors: 'Gerenciar fornecedores e relacionamentos',
    addVendor: 'Adicionar Fornecedor',
    allVendors: 'Todos os Fornecedores',
    searchVendors: 'Buscar fornecedores...',
    noVendors: 'Nenhum fornecedor encontrado',
    contact: 'Contato',
  },
  ar: {
    title: 'Contas a Receber',
    invoices: 'Notas Fiscais',
    invoicesDescription: 'Gerenciar notas fiscais de clientes',
    newInvoice: 'Nova NF-e',
    createInvoice: 'Criar NF-e',
    editInvoice: 'Editar NF-e',
    customer: 'Cliente',
    selectCustomer: 'Selecionar cliente',
    invoiceNumber: 'Número da NF-e',
    issueDate: 'Data de Emissão',
    dueDate: 'Data de Vencimento',
    amount: 'Valor',
    paidAmount: 'Valor Pago',
    balance: 'Saldo',
    status: 'Situação',
    draft: 'Rascunho',
    sent: 'Enviada',
    viewed: 'Visualizada',
    paid: 'Paga',
    overdue: 'Vencida',
    void: 'Cancelada',
    customers: 'Clientes',
    customersDescription: 'Gerenciar relacionamentos com clientes',
    newCustomer: 'Novo Cliente',
    createCustomer: 'Criar Cliente',
    customerName: 'Nome do Cliente',
    contactName: 'Nome do Contato',
    email: 'E-mail',
    phone: 'Telefone',
    address: 'Endereço',
    city: 'Cidade',
    state: 'Estado',
    zipCode: 'CEP',
    country: 'País',
    creditLimit: 'Limite de Crédito',
    openBalance: 'Saldo em Aberto',
    totalRevenue: 'Receita Total',
    openReceivables: 'Recebíveis em Aberto',
    lifetimeRevenue: 'Receita Acumulada',
    manageCustomers: 'Gerenciar clientes e relacionamentos de crédito',
    addCustomer: 'Adicionar Cliente',
    allCustomers: 'Todos os Clientes',
    searchCustomers: 'Buscar clientes...',
    noCustomers: 'Nenhum cliente encontrado',
    contact: 'Contato',
    createNFe: 'Criar NF-e',
  },
  ai: {
    automation: 'Automação com IA',
    realTimeMetrics: 'Métricas em tempo real',
    allSystemsOperational: 'Todos os Sistemas Operacionais',
    touchlessProcessing: 'Processamento Automático',
    autoProcessed: 'Transações processadas automaticamente',
    categorization: 'Categorização por IA',
    accuracyRate: 'Taxa de precisão',
    receiptProcessing: 'Processamento de Recibos',
    avgExtractionTime: 'Tempo médio de extração',
    invoiceExtraction: 'Extração de NF-e',
    dataAccuracy: 'Precisão de dados',
    onTarget: 'Dentro da Meta',
    scanReceipt: 'Escanear Recibo',
    extractInvoice: 'Extrair NF-e',
    smartCategorize: 'Categorizar Automaticamente',
    poweredBy: 'Desenvolvido por Finova IA',
  },
};

type TranslationValue = string | Record<string, unknown>;
type Translations = Record<string, TranslationValue>;

function getNestedValue(obj: Translations, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  return typeof current === 'string' ? current : undefined;
}

interface MarketContextType {
  market: Market;
  config: MarketConfig;
  setMarket: (market: Market) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string) => string;
  formatNumber: (num: number) => string;
  formatTaxId: (id: string) => string;
  isBrazil: boolean;
  isUS: boolean;
}

const MarketContext = createContext<MarketContextType | undefined>(undefined);

interface MarketProviderProps {
  children: React.ReactNode;
  defaultMarket?: Market;
}

export function MarketProvider({ children, defaultMarket = 'US' }: MarketProviderProps) {
  const [market, setMarketState] = useState<Market>(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('finova_market');
    return (saved === 'US' || saved === 'BR') ? saved : defaultMarket;
  });

  const config = MARKET_CONFIGS[market];

  // Persist market selection
  useEffect(() => {
    localStorage.setItem('finova_market', market);
  }, [market]);

  const setMarket = useCallback((newMarket: Market) => {
    setMarketState(newMarket);
  }, []);

  // Translation function
  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const translations = market === 'BR' ? (ptBR as Translations) : (enUS as Translations);
      let value: string = getNestedValue(translations, key) ?? '';

      // Fallback to English if not found in Portuguese
      if (!value && market === 'BR') {
        value = getNestedValue(enUS as Translations, key) ?? '';
      }

      // Final fallback to key
      if (!value) {
        const parts = key.split('.');
        value = parts[parts.length - 1] ?? key;
      }

      // Replace parameters
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          value = value.replace(`{${paramKey}}`, String(paramValue));
        });
      }

      return value;
    },
    [market]
  );

  // Currency formatter
  const formatCurrency = useCallback(
    (amount: number): string => {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
      }).format(amount);
    },
    [config]
  );

  // Date formatter
  const formatDate = useCallback(
    (date: Date | string): string => {
      return new Intl.DateTimeFormat(config.locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(date));
    },
    [config]
  );

  // Number formatter
  const formatNumber = useCallback(
    (num: number): string => {
      return new Intl.NumberFormat(config.locale).format(num);
    },
    [config]
  );

  // Tax ID formatter (CNPJ or EIN)
  const formatTaxId = useCallback(
    (id: string): string => {
      const cleaned = id.replace(/\D/g, '');

      if (market === 'BR') {
        // CNPJ: 12.345.678/0001-99
        if (cleaned.length === 14) {
          return cleaned.replace(
            /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
            '$1.$2.$3/$4-$5'
          );
        }
        // CPF: 123.456.789-01
        if (cleaned.length === 11) {
          return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
        }
      } else {
        // EIN: 12-3456789
        if (cleaned.length === 9) {
          return cleaned.replace(/^(\d{2})(\d{7})$/, '$1-$2');
        }
      }

      return id;
    },
    [market]
  );

  const value = useMemo(
    () => ({
      market,
      config,
      setMarket,
      t,
      formatCurrency,
      formatDate,
      formatNumber,
      formatTaxId,
      isBrazil: market === 'BR',
      isUS: market === 'US',
    }),
    [market, config, setMarket, t, formatCurrency, formatDate, formatNumber, formatTaxId]
  );

  return (
    <MarketContext.Provider value={value}>{children}</MarketContext.Provider>
  );
}

export function useMarket(): MarketContextType {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  return context;
}

// Export market configs for use elsewhere
export { MARKET_CONFIGS };
export type { MarketConfig };
